import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { taskPhotoService } from '../../services/taskPhotoService';
import { siteService } from '../../services/siteService';
import { useAuth } from '../../contexts/AuthContext';
import GalleryFilters from './components/GalleryFilters';
import PhotoGrid from './components/PhotoGrid';
import PhotoDetailModal from './components/PhotoDetailModal';
import GalleryStats from './components/GalleryStats';

const SitePhotoGallery = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [photos, setPhotos] = useState([]);
  const [sites, setSites] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    siteId: 'all',
    uploadedBy: 'all',
    dateFrom: '',
    dateTo: '',
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [photosRes, sitesRes] = await Promise.all([
        taskPhotoService?.getAllPhotos(filters),
        siteService?.getSitesByUser?.(),
      ]);

      if (photosRes?.error) throw photosRes?.error;
      setPhotos(photosRes?.data || []);

      const sitesData = sitesRes?.data || [];
      setSites(sitesData);

      // Extract unique workers from photos
      const workerMap = new Map();
      (photosRes?.data || [])?.forEach(p => {
        if (p?.uploadedBy && p?.uploader?.full_name) {
          workerMap?.set(p?.uploadedBy, { id: p?.uploadedBy, full_name: p?.uploader?.full_name });
        }
      });
      setWorkers(Array.from(workerMap?.values()));
    } catch (err) {
      console.error('[SitePhotoGallery] load photos failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setError(`Permission denied loading photos: ${err?.message}`);
      } else {
        setError(`Failed to load photos: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredPhotos = useMemo(() => {
    if (!searchQuery?.trim()) return photos;
    const q = searchQuery?.toLowerCase();
    return photos?.filter(p =>
      p?.task?.title?.toLowerCase()?.includes(q) ||
      p?.uploader?.full_name?.toLowerCase()?.includes(q) ||
      p?.site?.site_name?.toLowerCase()?.includes(q) ||
      p?.task?.category?.toLowerCase()?.includes(q)
    );
  }, [photos, searchQuery]);

  const handlePhotoClick = (photo, url) => {
    setSelectedPhoto(photo);
    setSelectedPhotoUrl(url);
  };

  const handleDeletePhoto = async (photoId, filePath) => {
    const { error: delErr } = await taskPhotoService?.deletePhoto(photoId, filePath);
    if (delErr) {
      setError(delErr?.code === 'PGRST301' || delErr?.code === '42501'
        ? `Permission denied deleting photo: ${delErr?.message}`
        : `Failed to delete photo: ${delErr?.message || 'Unknown error'}`);
      return;
    }
    setPhotos(prev => prev?.filter(p => p?.id !== photoId));
  };

  const canManage = userProfile?.role === 'admin' || userProfile?.role === 'site_manager';

  const handleToggleClientVisible = async (photoId, next) => {
    setPhotos(prev => prev?.map(p => p?.id === photoId ? { ...p, clientVisible: next } : p));
    const { error: updErr } = await taskPhotoService?.setClientVisible(photoId, next);
    if (updErr) {
      setPhotos(prev => prev?.map(p => p?.id === photoId ? { ...p, clientVisible: !next } : p));
      if (updErr?.code === 'PGRST301' || updErr?.code === '42501' || updErr?.code === 'NO_ROW_AFFECTED') {
        setError(`Permission denied updating visibility: ${updErr?.message}`);
      } else {
        setError(`Failed to update client visibility: ${updErr?.message || 'Unknown error'}`);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Site Photo Gallery | SiteProof</title>
      </Helmet>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Site Photo Gallery</h1>
            <p className="text-sm text-muted-foreground mt-1">Visual documentation from across all sites</p>
          </div>
          <button
            onClick={() => navigate('/task-completion-workflow')}
            className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-xl text-sm font-medium hover:bg-muted/80 transition-colors flex-shrink-0"
          >
            <Icon name="ArrowLeft" size={15} /> Tasks
          </button>
        </div>

        {/* Stats */}
        <GalleryStats photos={filteredPhotos} />

        {/* Filters */}
        <div className="mt-5">
          <GalleryFilters
            filters={filters}
            onChange={setFilters}
            sites={sites}
            workers={workers}
            loading={loading}
          />
        </div>

        {/* Search */}
        <div className="mt-4 relative">
          <Icon name="Search" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by task name, worker, site, or category..."
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent/30 text-foreground placeholder:text-muted-foreground"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={14} />
            </button>
          )}
        </div>

        {/* Results count */}
        {!loading && (
          <div className="flex items-center justify-between mt-4 mb-3">
            <p className="text-sm text-muted-foreground">
              {filteredPhotos?.length} photo{filteredPhotos?.length !== 1 ? 's' : ''}
              {searchQuery ? ` matching "${searchQuery}"` : ''}
            </p>
            <button
              onClick={loadData}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="RefreshCw" size={12} /> Refresh
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
            <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={loadData} className="ml-auto text-xs text-red-600 underline">Retry</button>
          </div>
        )}

        {/* Photo grid */}
        <PhotoGrid
          photos={filteredPhotos}
          onPhotoClick={handlePhotoClick}
          loading={loading}
          canManage={canManage}
          onToggleClientVisible={handleToggleClientVisible}
        />
      </main>

      {/* Photo detail modal */}
      {selectedPhoto && (
        <PhotoDetailModal
          photo={selectedPhoto}
          onClose={() => { setSelectedPhoto(null); setSelectedPhotoUrl(null); }}
          onDelete={handleDeletePhoto}
          canDelete={canManage}
          canManage={canManage}
          onToggleClientVisible={handleToggleClientVisible}
        />
      )}
    </div>
  );
};

export default SitePhotoGallery;
